import React, { useState } from "react";
import { Mail, Instagram, Phone, Youtube, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { s } from "@/i18n/translations";

const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.68a8.16 8.16 0 0 0 4.77 1.52V6.79a4.85 4.85 0 0 1-1.84-.1Z"/>
  </svg>
);

const WhatsAppIcon = ({ size = 20, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
  </svg>
);

const PHONE_DISPLAY = "+39 320 617 9277";
const PHONE_RAW = "393206179277";

export default function Contacts() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", artist_name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source_page: "contact" });
      setSent(true);
      toast.success(s("form.success", lang));
    } catch (err) {
      toast.error(s("form.error", lang));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contacts-page">
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-16 pb-12">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-4">— Contatti</div>
        <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] text-[#0A0A0A]">
          {t("contact.title", "Contatti")}
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A]/70">{t("contact.subtitle", "")}</p>
      </section>

      <section className="border-t-2 border-[#0A0A0A]">
        <div className="mx-auto max-w-[1400px] grid lg:grid-cols-3">
          <div className="p-10 border-r-0 lg:border-r-2 border-[#0A0A0A] bg-[#F2F2EC] space-y-8">
            <div data-testid="contact-block-email">
              <Mail className="text-[#FF5A1F]" />
              <div className="mt-3 text-xs uppercase tracking-widest text-[#0A0A0A]/60">Email</div>
              <a href="mailto:info.perlamgmt@gmail.com" className="font-display text-2xl text-[#0A0A0A] link-underline" data-testid="contact-email-link">
                info.perlamgmt@gmail.com
              </a>
            </div>

            <div data-testid="contact-block-phone">
              <Phone className="text-[#FF5A1F]" />
              <div className="mt-3 text-xs uppercase tracking-widest text-[#0A0A0A]/60">{lang === "en" ? "Phone" : "Telefono"}</div>
              <a href={`tel:${PHONE_RAW}`} className="font-display text-2xl text-[#0A0A0A] link-underline" data-testid="contact-phone-link">
                {PHONE_DISPLAY}
              </a>
            </div>

            <div data-testid="contact-block-whatsapp">
              <WhatsAppIcon className="text-[#FF5A1F]" />
              <div className="mt-3 text-xs uppercase tracking-widest text-[#0A0A0A]/60">WhatsApp</div>
              <a href={`https://wa.me/${PHONE_RAW}`} target="_blank" rel="noreferrer" className="font-display text-2xl text-[#0A0A0A] link-underline" data-testid="contact-whatsapp-link">
                {PHONE_DISPLAY}
              </a>
            </div>

            <div data-testid="contact-block-instagram">
              <Instagram className="text-[#FF5A1F]" />
              <div className="mt-3 text-xs uppercase tracking-widest text-[#0A0A0A]/60">Instagram</div>
              <a href="https://instagram.com/perlamusicmgmt" target="_blank" rel="noreferrer" className="font-display text-2xl text-[#0A0A0A] link-underline" data-testid="contact-ig-link">
                {t("contact.instagram", "@perlamusicmgmt")}
              </a>
            </div>

            <div data-testid="contact-block-tiktok">
              <TikTokIcon className="text-[#FF5A1F]" />
              <div className="mt-3 text-xs uppercase tracking-widest text-[#0A0A0A]/60">TikTok</div>
              <a href="https://www.tiktok.com/@perla.music.mgmt" target="_blank" rel="noreferrer" className="font-display text-2xl text-[#0A0A0A] link-underline" data-testid="contact-tiktok-link">
                @perla.music.mgmt
              </a>
            </div>

            <div data-testid="contact-block-youtube">
              <Youtube className="text-[#FF5A1F]" />
              <div className="mt-3 text-xs uppercase tracking-widest text-[#0A0A0A]/60">YouTube</div>
              <a href="https://www.youtube.com/@PerlaMusic.Management" target="_blank" rel="noreferrer" className="font-display text-2xl text-[#0A0A0A] link-underline" data-testid="contact-youtube-link">
                @PerlaMusic.Management
              </a>
            </div>
          </div>

          <div className="lg:col-span-2 p-10">
            {sent ? (
              <div className="h-full flex flex-col items-start justify-center" data-testid="contact-success">
                <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">✓ Ricevuto</div>
                <h2 className="mt-4 font-display text-5xl md:text-6xl uppercase text-[#0A0A0A]">Grazie.</h2>
                <p className="mt-4 text-lg text-[#0A0A0A]/70 max-w-md">{s("form.success", lang)}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <Field label={s("form.name", lang)} value={form.name} onChange={(v) => onChange("name", v)} required testId="contact-name" />
                <Field label={s("form.email", lang)} type="email" value={form.email} onChange={(v) => onChange("email", v)} required testId="contact-email" />
                <Field label={s("form.phone", lang)} value={form.phone} onChange={(v) => onChange("phone", v)} required testId="contact-phone" />
                <Field label={s("form.artist_name", lang)} value={form.artist_name} onChange={(v) => onChange("artist_name", v)} testId="contact-artist" />
                <TextArea label={s("form.message", lang)} value={form.message} onChange={(v) => onChange("message", v)} testId="contact-message" />
                <button type="submit" disabled={submitting} className="btn-cta disabled:opacity-60" data-testid="contact-submit">
                  <Send size={16} /> {s("form.submit_contact", lang)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testId }) => (
  <label className="block group">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}{required && " *"}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      data-testid={testId}
      className="mt-2 block w-full bg-transparent border-0 border-b-2 border-[#0A0A0A] py-2 px-0 text-lg text-[#0A0A0A] focus:outline-none focus:border-[#FF5A1F] transition-colors"
    />
  </label>
);

const TextArea = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      data-testid={testId}
      className="mt-2 block w-full bg-transparent border-2 border-[#0A0A0A] py-3 px-3 text-base text-[#0A0A0A] focus:outline-none focus:border-[#FF5A1F] transition-colors resize-none"
    />
  </label>
);
